import type { IntelReportWire, PhaseState } from '@badger-sentinel/protocol';
import { useEffect, useMemo } from 'react';

import { ArchiveRail } from './ArchiveRail.js';
import { DeltaPanel } from './DeltaPanel.js';
import { EntryGroup } from './EntryGroup.js';
import { OrdersBlock } from './OrdersBlock.js';
import { archiveTurns, readerModel, readerTurn } from '../model/intel.js';
import {
  intelStateFor,
  reportFor,
  shouldRequest,
  type IntelArchiveState,
} from '../model/intel-archive.js';
import { intelDelta } from '../model/intel-delta.js';
import { useConsoleStore } from '../state/store.js';
import { DATA_LABEL, DATA_TAG, READOUT } from '../../theme/type.js';

/**
 * The intel centre pane (design 470–723).
 *
 * The one module in this console with **no renderer to vendor**. Phases 4–7 each
 * opened by reading somebody else's D3 file and deciding how much of it could be
 * trusted; this one opened by reading a mock data structure and deciding how much
 * of it the wire could pay for. `model/intel.ts` has that table.
 *
 * Its shape is `mw/MwView.tsx`'s, which is the one existing module that is a
 * *reader* rather than a canvas: a header band, a rail beside the body, and a
 * dock underneath. The rails either side of the centre pane are the shell's on
 * every tab (shell.md's split), so the archive is a rail **inside** this pane
 * exactly as the launch-event rail is.
 *
 * ## The turn a report is about is not the turn you are in
 *
 * `report.turn` is the turn the report is **about**. A report for turn *n*
 * arrives when turn *n+1* opens — `broadcaster.ts` builds it from
 * `game.lastResolvedTurn` — so the newest report is always `phase.turn − 1`, and
 * the fixture directory `turn-2/intel.BLUFOR.CW.json` carries `turn: 1` for
 * exactly that reason. Every label, every archive row and the delta's basis hang
 * off getting that the right way round, so it is derived in one place
 * (`readerTurn`) and never re-derived.
 *
 * ## What requests what
 *
 * Two turns are asked for and no others: the one being read, and the one before
 * it for the delta. Nothing is fetched by scrolling the rail — see
 * `ArchiveRail`. The live report is never re-fetched, because it arrived on the
 * socket already projected for this seat.
 */

export interface IntelViewProps {
  readonly phase: PhaseState | null;
  /** The live report — the newest turn's, from `intel.report`. Already viewer-guarded. */
  readonly report: IntelReportWire | null;
  readonly archive: ReadonlyMap<number, IntelArchiveState>;
  readonly onRequest: (turn: number) => void;
  /** Clicking an actor or a contact selects it, which opens the right rail. */
  readonly onSelectSubject: (id: string) => void;
}

/**
 * The pane's floor — a **floor** here rather than a height, which is where this
 * module parts company with `MW_MIN_HEIGHT`.
 *
 * The MW board is bounded: a rail, a map, a dock. This one is a *document* whose
 * length is however many entries the turn produced, so it grows past this and
 * the centre pane's own column scrolls. What the floor buys is the empty case —
 * a quiet turn's `NOTHING REPORTED` sitting in a pane the same size as a busy
 * turn's, rather than a caption in a 60 px strip.
 */
export const INTEL_MIN_HEIGHT = 420;

export function IntelView(props: IntelViewProps): JSX.Element {
  const cursor = useConsoleStore((state) => state.intelTurn);
  const readTurn = useConsoleStore((state) => state.readIntelTurn);
  const closedGroups = useConsoleStore((state) => state.intelClosedGroups);
  const toggleGroup = useConsoleStore((state) => state.toggleIntelGroup);
  const deltaOpen = useConsoleStore((state) => state.intelDeltaOpen);
  const setDeltaOpen = useConsoleStore((state) => state.setIntelDeltaOpen);

  const currentTurn = props.phase?.turn ?? null;
  const turn = readerTurn(cursor, currentTurn);
  const turns = useMemo(() => archiveTurns(currentTurn), [currentTurn]);

  const state: IntelArchiveState | null =
    turn === null ? null : intelStateFor(turn, props.report, props.archive);
  const report = state !== null && state.status === 'ready' ? state.report : null;

  // The two requests this pane makes, and they are the only two. `shouldRequest`
  // is what keeps a `failed` turn from looping: the retry is the operator's.
  const onRequest = props.onRequest;
  const archive = props.archive;
  const live = props.report;
  useEffect(() => {
    if (turn === null) return;
    if (shouldRequest(intelStateFor(turn, live, archive))) onRequest(turn);
    const before = turn - 1;
    if (before >= 1 && shouldRequest(intelStateFor(before, live, archive))) onRequest(before);
  }, [turn, live, archive, onRequest]);

  const previous = turn === null ? null : reportFor(turn - 1, live, archive);
  const delta = useMemo(
    () => (report === null ? null : intelDelta(report, previous)),
    [report, previous],
  );

  // One model, so the orders block and the category groups cannot draw the same
  // entry twice — which they did, in a browser, on the first real report.
  const model = useMemo(() => (report === null ? null : readerModel(report)), [report]);
  const summary = model?.summary ?? null;

  const older = turn === null ? null : turn - 1 >= 1 ? turn - 1 : null;
  const newest = currentTurn === null ? null : currentTurn - 1;
  const newer = turn === null || newest === null ? null : turn + 1 <= newest ? turn + 1 : null;

  return (
    <div
      data-testid="intel-view"
      style={{
        position: 'relative',
        flex: '1 1 auto',
        minHeight: INTEL_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--canvas)',
      }}
    >
      <div
        data-testid="intel-header"
        data-mat="chrome hdr"
        style={{
          // Sticky, because the report is the thing that scrolls and the turn it
          // is about must not scroll away from it. `←` and `→` go with it: a key
          // whose effect is a label three screens up is a key nobody trusts.
          position: 'sticky',
          top: 0,
          zIndex: 'var(--z-panel-sticky)',
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span style={{ ...DATA_TAG, flex: 'none', color: 'var(--text-muted)' }}>REPORT</span>
        {/* The turn the report is *about*. Never `phase.turn`. Sized down from the
            masthead's 23px READOUT to fit this 33px band — the letter-spacing
            still departs from READOUT's none, because a turn number this small
            reads better held together than the masthead's does. */}
        <span
          data-testid="intel-turn"
          style={{
            ...READOUT,
            flex: 'none',
            fontSize: 15,
            letterSpacing: '.04em',
            color: turn === null ? 'var(--text-disabled)' : 'var(--text)',
          }}
        >
          {turn === null ? '—' : `TURN ${String(turn).padStart(2, '0')}`}
        </span>

        <Step
          testId="intel-older"
          label="◀"
          turn={older}
          hint="OLDER"
          onGo={(next) => readTurn(next)}
        />
        <Step
          testId="intel-newer"
          label="▶"
          turn={newer}
          hint="NEWER"
          onGo={(next) => readTurn(next === newest ? null : next)}
        />

        {summary !== null && summary.critical > 0 && (
          <span
            data-testid="intel-kicker-critical"
            style={{
              ...DATA_TAG,
              flex: 'none',
              fontSize: 9.5,
              padding: '1px 7px',
              border: '1px solid var(--red-text)',
              borderRadius: 'var(--radius)',
              color: 'var(--red-text)',
            }}
          >
            {summary.critical} CRITICAL
          </span>
        )}
        {summary !== null && summary.priority > 0 && (
          <span
            data-testid="intel-kicker-priority"
            style={{
              ...DATA_TAG,
              flex: 'none',
              fontSize: 9.5,
              padding: '1px 7px',
              border: '1px solid var(--warn)',
              borderRadius: 'var(--radius)',
              color: 'var(--warn)',
            }}
          >
            {summary.priority} PRIORITY
          </span>
        )}

        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="intel-delta-toggle"
          aria-pressed={deltaOpen}
          onClick={() => setDeltaOpen(!deltaOpen)}
          style={CHIP}
        >
          DELTA
        </button>
      </div>

      {/* Above the report rather than under it. The delta is a **headline about
          the turn** — five figures and a basis — and a dock at the bottom of a
          document is a dock nobody scrolls to. Seen in a browser: the report ran
          past the fold and the panel with it. */}
      {deltaOpen && delta !== null && <DeltaPanel delta={delta} />}

      {/* One scroll on this tab, and it is the centre pane's. A reader that
          scrolled inside itself put the report in a 290 px window on a 1080 px
          screen — the pane's own column already scrolls, and nesting a second
          scroll inside it is the wrong shape for a document. */}
      <div
        style={{
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'stretch',
          gap: 1,
          background: 'var(--border)',
        }}
      >
        <ArchiveRail
          turns={turns}
          selected={turn}
          stateOf={(candidate) => intelStateFor(candidate, live, archive)}
          onSelect={(candidate) => readTurn(candidate === newest ? null : candidate)}
        />

        <div
          data-testid="intel-reader"
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface)',
          }}
        >
          {report === null ? (
            <ReaderState turn={turn} state={state} onRetry={props.onRequest} />
          ) : report.entries.length === 0 ? (
            // "A turn with no entries renders as empty, not as missing." This is
            // a statement about a quiet turn and is the whole reason `absent` is a
            // different answer.
            <Notice testId="intel-empty" title="NOTHING REPORTED" />
          ) : (
            <>
              <OrdersBlock actions={model?.actions ?? []} onSelect={props.onSelectSubject} />
              {(model?.groups ?? []).map((group) => (
                <EntryGroup
                  key={group.category}
                  group={group}
                  open={!closedGroups.has(group.category)}
                  onToggle={() => toggleGroup(group.category)}
                  onSelect={props.onSelectSubject}
                />
              ))}
              {summary !== null && (
                <div
                  data-testid="intel-footer"
                  style={{
                    ...DATA_TAG,
                    flex: 'none',
                    marginTop: 'auto',
                    display: 'flex',
                    gap: 16,
                    padding: '8px 12px',
                    borderTop: '1px solid var(--border)',
                    color: 'var(--text-muted)',
                  }}
                >
                  <span>
                    {summary.entries} {summary.entries === 1 ? 'ENTRY' : 'ENTRIES'}
                  </span>
                  <span>
                    {summary.categories} {summary.categories === 1 ? 'CATEGORY' : 'CATEGORIES'}
                  </span>
                  {/* The only time on this screen: the resolution phases the
                      entries fall in. No clock, because there is none. */}
                  <span data-testid="intel-phase-span">
                    {summary.phaseFrom === summary.phaseTo
                      ? `PHASE ${String(summary.phaseFrom)}`
                      : `PHASES ${String(summary.phaseFrom)}–${String(summary.phaseTo)}`}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The four non-`ready` states, each with its own words.
 *
 * `turn === null` is a fifth thing and not one of the archive's: there is no
 * resolved turn at all, which on turn 1 is RB §2.2 ("skipped during the first
 * turn") rather than anything having gone wrong.
 */
function ReaderState(props: {
  turn: number | null;
  state: IntelArchiveState | null;
  onRetry: (turn: number) => void;
}): JSX.Element {
  if (props.turn === null || props.state === null) {
    return <Notice testId="intel-none" title="NO REPORT BEFORE TURN 02" />;
  }
  switch (props.state.status) {
    case 'idle':
      return <Notice testId="intel-idle" title="NOT REQUESTED" />;
    case 'loading':
      return <Notice testId="intel-loading" title="REQUESTING" />;
    case 'absent':
      return <Notice testId="intel-absent" title="TURN NOT RESOLVED" />;
    case 'forbidden':
      return <Notice testId="intel-forbidden" title="NO REPORT FOR THIS SEAT" />;
    case 'failed':
      return (
        <Notice testId="intel-failed" title={'NOT FETCHED · ' + props.state.reason}>
          <button
            type="button"
            data-testid="intel-retry"
            onClick={() => props.onRetry(props.turn ?? 0)}
            style={CHIP}
          >
            RETRY
          </button>
        </Notice>
      );
    case 'ready':
      return <Notice testId="intel-empty" title="NOTHING REPORTED" />;
  }
}

function Notice(props: { testId: string; title: string; children?: React.ReactNode }): JSX.Element {
  return (
    <div
      data-testid={props.testId}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 24,
      }}
    >
      <span style={{ ...DATA_TAG, fontSize: 11, color: 'var(--text-muted)' }}>{props.title}</span>
      {props.children}
    </div>
  );
}

/** `◀` / `▶`, withdrawn rather than disabled where there is no turn to go to. */
function Step(props: {
  testId: string;
  label: string;
  hint: string;
  turn: number | null;
  onGo: (turn: number) => void;
}): JSX.Element | null {
  const turn = props.turn;
  if (turn === null) return null;
  return (
    <button
      type="button"
      data-testid={props.testId}
      title={props.hint}
      aria-label={props.hint}
      onClick={() => props.onGo(turn)}
      style={{ ...CHIP, minWidth: 26, textAlign: 'center' }}
    >
      {props.label}
    </button>
  );
}

const CHIP = {
  ...DATA_LABEL,
  flex: 'none',
  height: 22,
  padding: '0 9px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
} as const;
